import { Injectable } from "@angular/core";

import { map, mergeMap, tap } from "rxjs";

import {
  NgxsAfterBootstrap,
  Action,
  Selector,
  State,
  StateContext,
  StateToken,
  createSelector,
  Store,
} from "@ngxs/store";
import { patch } from "@ngxs/store/operators";
import { upsertItem } from "../../operators/ngxs-upsertItem";

import { AuthState } from "@dis/auth";
import { BaseCollectionsState } from "../../../states/base.state";
import { appAfterBootstrap } from "../../operators/afterBootstrap";

import { TableQuery, TableState } from "../../../components/tables";

import { QueryClient } from "@tanstack/angular-query-experimental";

/**
 *  ~~~~ ONLY EDIT BETWEEN THESE COMMENTS ~~~~
 */

import { COLLECTIONS, Project as Model } from "@dis/models";
import { ProjectsSheetStore as SheetStore } from "../services/sheetstore";
import { ProjectsStateActions as StateActions } from "./actions";

import { injectProjectUpdateByNumber } from "@jtms/default-connector/angular";

const COLLECTION = COLLECTIONS.PROJECTS;

export { type StateModel as ProjectsStateModel, STATE_TOKEN as PROJECTS_STATE_TOKEN, StateStore as ProjectsState };

/**
 * ^^^^ ONLY EDIT BETWEEN THESE COMMENTS ^^^^
 */

const STATE_TOKEN = new StateToken<StateModel>(`${COLLECTION.toLowerCase()}sState`);

interface StateModel {
  loading: boolean;
  items: Model[];
  filterText: string | null;
}

@State({
  name: STATE_TOKEN,
  defaults: {
    loading: false,
    items: [],
    filterText: null,
  },
})
@Injectable()
class StateStore extends BaseCollectionsState implements NgxsAfterBootstrap {
  /**
   *  DO NOT ADD SELCTORS HERE USE THE "stateName.selectors.ts file"
   */

  @Selector([STATE_TOKEN])
  static items(state: StateModel) {
    return state.items;
  }

  @Selector([STATE_TOKEN])
  static count(state: StateModel) {
    return state.items.length;
  }

  @Selector([StateStore.items, TableState.tableQuery(COLLECTION)])
  static tableItems(items: Model[], { active, direction }: TableQuery) {
    return this.sortByActive(items, active, direction);
  }

  @Selector([StateStore.tableItems, TableState.tableQuery(COLLECTION)])
  static filtered(items: Model[], { filterText }: TableQuery) {
    return filterText ? [...items.filter((item) => this.filterPredicate(item, filterText))] : [...items];
  }

  static getById(uid: string) {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter((item) => item.uid === uid)[0];
    });
  }

  /**
   * @deprecated moving to getByStatuses so can provide more than one status
   * @param  {string} status
   */
  static getByStatus(status: string) {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter((item) => item.status === status);
    });
  }

  static getByStatuses(statuses: string[]) {
    return createSelector([StateStore.items], (items: Model[]) => {
      return items.filter((item) => item.status !== null && statuses.includes(item.status));
    });
  }

  constructor(
    private db: SheetStore,
    private store: Store,
    private queryClient: QueryClient
  ) {
    super();
  }

  ngxsAfterBootstrap({ dispatch, patchState }: StateContext<any>): void {
    patchState({ loading: true });
    this.store
      .select(AuthState.hasGapiToken)
      .pipe(
        appAfterBootstrap(),
        tap(() => dispatch(new StateActions.GetAll()))
      )
      .subscribe();
  }

  @Action(StateActions.FilterList, { cancelUncompleted: true })
  filterList({ patchState }: StateContext<StateModel>, { payload: filterText }: StateActions.FilterList) {
    patchState({ filterText });
  }

  @Action(StateActions.GetAll, { cancelUncompleted: true })
  getAll({ dispatch, patchState }: StateContext<StateModel>) {
    patchState({ loading: true });
    return this.db.collection$().pipe(
      map((items) => items.map((item) => new Model(item))),
      tap((items) =>
        patchState({
          loading: false,
          items,
        })
      ),
      mergeMap(() => dispatch(new StateActions.StartCollectionSheetWatcher()))
    );
  }

  @Action(StateActions.StartCollectionSheetWatcher)
  async watchCollection({ dispatch, patchState }: StateContext<StateModel>) {
    patchState({ loading: true });
    return this.db.docs$().pipe(mergeMap((items) => dispatch(new StateActions.UpdateFromSheetWatcher(items))));
  }

  /**
   * @deprecated should only be used internally within the states shared library
   * @param param0
   * @returns
   */
  @Action(StateActions.UpdateFromSheetWatcher)
  updateState(
    { patchState, setState }: StateContext<StateModel>,
    { payload: items }: StateActions.UpdateFromSheetWatcher
  ) {
    items.map((_item) => {
      _item = new Model(_item);
      setState(
        patch<StateModel>({
          items: upsertItem<Model>((item) => item.uid === _item.uid, _item),
        })
      );
    });
    patchState({ loading: false });
  }

  /**
   *
   *  Initial implementaation of adding Dataconnect
   */
  upsertProjectByNumber = injectProjectUpdateByNumber(() => ({
    mutationKey: ["project"],
  }));

  async #upsertDataconnect(item: Partial<Model> & { rigString?: string }) {
    if (!item.projectNumber) return item.dcid;
    try {
      const dcid = await this.upsertProjectByNumber.mutateAsync({
        projectNumber: item.projectNumber,
        areaOfOperation: item.areaOfOperation,
        customer: item.customer,
        folderId: item.folderId,
        location: item.location,
        logId: item.logId,
        network: item.network,
        notes: item.notes,
        ocsgLease: item.ocsgLease,
        rigString: item.rigString,
        sections: item.sections,
        space: item.space,
        spudDate: this._checkIfTimestampOrDate(item.spudDate),
        stagingBoolean: Boolean(item.stagingBoolean),
        status: item.status,
        metadata: item.metadata,
        well: item.well,
      });
      item.dcid = dcid.project_upsert.id;
    } catch (e) {
      console.error("Error upserting project to dataconnect:", e);
    }
    return item.dcid;
  }

  /**
   *
   *
   * Below are actions that dispatch CRUD operations to the db handling the collection
   *
   */
  @Action(StateActions.Create)
  async create({ getState, patchState }: StateContext<StateModel>, { payload }: StateActions.Create) {
    patchState({ loading: true });

    const item = Model.toSpreadsheet(payload);

    // const dcid = await this.#upsertDataconnect(payload);
    // item.dcid = dcid;

    console.log("Project Item in State Create Action:", item);
    await this.db.upsertWithSheetsApi(undefined, item);

    patchState({ loading: false });
  }

  @Action(StateActions.Update)
  async update({ getState, patchState }: StateContext<StateModel>, { payload }: StateActions.Update) {
    patchState({ loading: true });

    const { rowIndex = null, ...currentState } = getState().items.find(
      (item) => item.projectNumber === payload.projectNumber
    )!;
    const mergedItem = {
      ...currentState,
      ...payload,
      rowIndex,
    } as Model;

    const item = Model.toSpreadsheet(mergedItem);

    console.log("Project Item in State Update Action:", item);
    await this.db.upsertWithSheetsApi(item.projectNumber, item);

    patchState({ loading: false });
  }

  @Action(StateActions.PartialUpdate)
  async partialUpdate(
    { getState, patchState }: StateContext<StateModel>,
    { uid, payload }: StateActions.PartialUpdate
  ) {
    patchState({ loading: true });

    const { rowIndex = null, ...currentState } = getState().items.find(
      (item) => item.projectNumber === payload.projectNumber
    )!;
    const mergedItem = {
      ...currentState,
      ...payload,
      rowIndex,
    } as Model;

    const item = Model.toSpreadsheet(mergedItem);

    // const dcid = await this.#upsertDataconnect(payload);
    // item.dcid = dcid;

    console.log("Project Item in State Update Action:", item);
    await this.db.upsertWithSheetsApi(item.projectNumber, item);

    patchState({ loading: false });
  }

  @Action(StateActions.Delete)
  async delete({ patchState }: StateContext<StateModel>, { payload }: StateActions.Delete) {
    patchState({ loading: true });

    const item = payload;
    await this.db.deleteFile(item.folderId!);
    await this.db.delete(item.uid!);

    patchState({ loading: false });
  }
}
